import { Flex, Spinner, Portal } from "@chakra-ui/react";
import TagStatus from "./TagStatus";
import { MdFileDownload, MdMarkEmailRead } from "react-icons/md";
import { FaHandsHelping } from "react-icons/fa";
import { GoAlert } from "react-icons/go";
import {
  AiOutlineClockCircle,
  AiOutlineCreditCard,
  AiOutlineRedo,
} from "react-icons/ai";
import { RiBarcodeFill } from "react-icons/ri";
import { ContaItemResponse } from "core/features/conta/typings";
import { StatusBoletoEnum } from "resources/enums/conta";
import { FormaPagamento } from "resources/enums/tenant";
import { OperacaoEnum } from "core/resources/enums/conta";
import { TipoAnexoContaEnum } from "core/features/conta/typings";
import { FaHandHoldingUsd } from "react-icons/fa";
import { AiOutlineCheck, AiOutlinePlus } from "react-icons/ai";
import { Menu, MenuButton, MenuItem, MenuList } from "@chakra-ui/menu";
import useTags from "core/features/tags/hooks/useTags";
import { FaCaretDown } from "react-icons/fa";
import useAlterarEtiquetaConta from "core/features/conta/hooks/useAlterarEtiquetaConta";
import { useState } from "react";
import { getTitleAnexoFromTipo } from "core/features/conta/util/getTitleAnexoFromTipo";
import { GiReceiveMoney } from "react-icons/gi";
interface ContentTagProps {
  item: ContaItemResponse;
  operacao: OperacaoEnum | undefined;
}

const ICON_SIZE = 15;

export default function ContentTag({ item, operacao }: ContentTagProps) {
  const {
    statusBoletoId,
    dataPagamento,
    acordo,
    formaPagamentoId,
    diasEmAtraso,
    linkBoleto,
    tiposAnexosIds,
    anexosTiposIds,
    pessoaLiquidacaoId,
  } = item;

  const [tag, setTag] = useState<string | null>(item.tag);

  const { data: tags, isLoading: isLoadingTags } = useTags();
  const {
    mutate: alterarEtiquetaConta,
    isLoading: isLoadingAlterarEtiquetaConta,
  } = useAlterarEtiquetaConta();

  const onChangeEtiqueta = (etiquetaId: string, etiquetaNome: string) => {
    alterarEtiquetaConta(
      { id: item.id, etiquetaId },
      {
        onSuccess: () => {
          setTag(etiquetaNome);
        },
      }
    );
  };

  return (
    <Flex gap="4px" alignItems="center">
      <Flex gap={2} mt={2} flexWrap="wrap">
        <TagStatusBoleto
          statusBoletoId={statusBoletoId}
          linkBoleto={linkBoleto}
          dataPagamento={dataPagamento}
        />

        <TagPagamento
          dataPagamento={dataPagamento}
          diasEmAtraso={diasEmAtraso}
        />

        {acordo ? (
          <TagStatus
            icon={<FaHandsHelping size={ICON_SIZE} />}
            title="Acordo"
            colorScheme={"green"}
          />
        ) : null}

        {item.emailEnviado ? (
          <TagStatus
            icon={<MdMarkEmailRead size={ICON_SIZE} />}
            colorScheme="green"
            title="Email enviado"
          />
        ) : null}

        {formaPagamentoId ? (
          <TagFormaPagamento formaPagamentoId={formaPagamentoId} />
        ) : null}

        {pessoaLiquidacaoId ? (
          <TagStatus
            icon={<FaHandHoldingUsd size={ICON_SIZE} />}
            title="Liquidação manual"
            colorScheme={"gray"}
          />
        ) : null}

        {item.contaProtestoId ? (
          <TagStatus
            colorScheme="red"
            title={"Protestado"}
            icon={<GiReceiveMoney fontSize={ICON_SIZE} />}
          />
        ) : null}

        {item.contaRecorrenteId ? (
          <TagStatus
            colorScheme="cyan"
            title={"Recorrente"}
            icon={<AiOutlineRedo fontSize={ICON_SIZE} />}
          />
        ) : null}

        {item.pagamentoDuplicado ? (
          <TagStatus colorScheme="yellow" title={"Pagamento duplicado"} />
        ) : null}

        {item.cobrancaUnificadaId ? (
          <TagStatus colorScheme="gray" title={"Cobrança unificada"} />
        ) : null}

        {item.importado ? (
          <TagStatus colorScheme="gray" title={"Importado"} />
        ) : null}

        {item.pagamentoManual && operacao === OperacaoEnum.Receita ? (
          <TagStatus colorScheme="gray" title={"Liquidação manual"} />
        ) : null}

        {operacao === OperacaoEnum.Despesa &&
        (tiposAnexosIds?.length || anexosTiposIds?.length) ? (
          <TagTipoAnexo
            tiposAnexosIds={tiposAnexosIds}
            anexosTiposIds={anexosTiposIds}
            pago={!!dataPagamento}
          />
        ) : null}
      </Flex>

      {tag ? (
        <Flex
          mt={2}
          ml={6}
          alignItems="center"
          gap={2}
          h="100%"
          id={`tag-menu-${item.id}`}
        >
          <Menu>
            <MenuButton
              disabled={isLoadingTags || isLoadingAlterarEtiquetaConta}
            >
              <TagStatus
                colorScheme="gray"
                title={tag || ""}
                icon={
                  isLoadingTags || isLoadingAlterarEtiquetaConta ? (
                    <Spinner size="xs" />
                  ) : (
                    <FaCaretDown size={ICON_SIZE} />
                  )
                }
              />
            </MenuButton>
            {tags?.length ? (
              <Portal>
                <MenuList>
                  {tags.map((tag) => (
                    <MenuItem
                      key={tag.id}
                      fontSize="14px"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        ev.preventDefault();
                        onChangeEtiqueta(tag.id, tag.nome);
                      }}
                    >
                      {tag.nome}
                    </MenuItem>
                  ))}
                </MenuList>
              </Portal>
            ) : null}
          </Menu>
        </Flex>
      ) : null}
    </Flex>
  );
}

const TagStatusBoleto = ({
  statusBoletoId,
  linkBoleto,
  dataPagamento,
}: {
  statusBoletoId?: StatusBoletoEnum;
  linkBoleto?: string | null;
  dataPagamento?: string | null;
}) => {
  return (
    <>
      {statusBoletoId === StatusBoletoEnum.AguardandoRegistro ? (
        <TagStatus
          title="Cobrança em registro"
          colorScheme="orange"
          icon={<AiOutlineClockCircle size={ICON_SIZE} />}
        />
      ) : null}

      {statusBoletoId === StatusBoletoEnum.Cancelado ? (
        <TagStatus
          title="Boleto Cancelado"
          colorScheme="red"
          icon={<GoAlert size={ICON_SIZE} />}
        />
      ) : null}

      {statusBoletoId === StatusBoletoEnum.Erro ? (
        <TagStatus
          title="Erro ao criar boleto"
          colorScheme="red"
          icon={<GoAlert size={ICON_SIZE} />}
        />
      ) : null}

      {statusBoletoId === StatusBoletoEnum.EmCancelamento && !dataPagamento ? (
        <TagStatus
          title="Boleto em cancelamento"
          colorScheme="yellow"
          icon={<GoAlert size={ICON_SIZE} />}
        />
      ) : null}

      {statusBoletoId === StatusBoletoEnum.Disponivel &&
      !!linkBoleto &&
      !dataPagamento ? (
        <TagStatus
          title="Boleto disponível"
          icon={<MdFileDownload size={ICON_SIZE} />}
          colorScheme="blue"
        />
      ) : null}
    </>
  );
};

const TagPagamento = ({
  dataPagamento,
  diasEmAtraso,
}: {
  dataPagamento?: string | null;
  diasEmAtraso?: number;
}) => {
  return (
    <>
      {dataPagamento ? (
        <TagStatus
          title={`Pago`}
          colorScheme="green"
        />
      ) : diasEmAtraso ? (
        <TagStatus
          title={`Em Atraso ${diasEmAtraso} ${
            diasEmAtraso === 1 ? "dia" : "dias"
          }`}
          icon={<GoAlert size={ICON_SIZE} />}
          colorScheme="red"
        />
      ) : (
        <TagStatus title={`Pendente`} colorScheme="orange" />
      )}
    </>
  );
};

const TagFormaPagamento = ({
  formaPagamentoId,
}: {
  formaPagamentoId?: FormaPagamento;
}) => {
  const colorScheme = "gray";

  switch (formaPagamentoId) {
    case FormaPagamento.BoletoBancario:
      return (
        <TagStatus
          title="Boleto"
          colorScheme={colorScheme}
          icon={<RiBarcodeFill size={ICON_SIZE} />}
        />
      );
    case FormaPagamento.Pix:
      return <TagStatus title="PIX" colorScheme={colorScheme} />;
    case FormaPagamento.CartaoCredito:
      return (
        <TagStatus
          title="Cartão de Crédito"
          colorScheme={colorScheme}
          icon={<AiOutlineCreditCard size={ICON_SIZE} />}
        />
      );
    case FormaPagamento.CartaoDebito:
      return (
        <TagStatus
          title="Cartão de Débito"
          colorScheme={colorScheme}
          icon={<AiOutlineCreditCard size={ICON_SIZE} />}
        />
      );

    case FormaPagamento.Cheque:
      return <TagStatus title="Cheque" colorScheme={colorScheme} />;

    case FormaPagamento.Transferencia:
      return <TagStatus title="Transferência" colorScheme={colorScheme} />;
  }

  return null;
};

const TagTipoAnexo = ({
  tiposAnexosIds, // Etiquetas da conta
  anexosTiposIds, // Tipos dos anexos da conta
  pago,
}: {
  tiposAnexosIds?: TipoAnexoContaEnum[];
  anexosTiposIds?: TipoAnexoContaEnum[];
  pago?: boolean;
}) => {
  const getIcon = (tipoAnexo: TipoAnexoContaEnum) => {
    if (
      tipoAnexo !== TipoAnexoContaEnum.Outros &&
      anexosTiposIds?.includes(tipoAnexo)
    ) {
      return <AiOutlineCheck size={ICON_SIZE} />;
    }

    return <AiOutlinePlus size={ICON_SIZE} />;
  };

  const getColorScheme = (tipoAnexo: TipoAnexoContaEnum) => {
    if (
      tipoAnexo !== TipoAnexoContaEnum.Outros &&
      anexosTiposIds?.includes(tipoAnexo)
    ) {
      return "green";
    }

    if (tipoAnexo !== TipoAnexoContaEnum.Outros) {
      return "red";
    }

    return "gray";
  };

  const items = Object.values(TipoAnexoContaEnum).map((tipoAnexo) => ({
    id: `tag-tipo-anexo-${tipoAnexo}`,
    shouldRender:
      tiposAnexosIds?.includes(tipoAnexo) ||
      anexosTiposIds?.includes(tipoAnexo),
    colorScheme: getColorScheme(tipoAnexo),
    icon: getIcon(tipoAnexo),
    title: getTitleAnexoFromTipo(tipoAnexo),
  }));

  const activeItems = items.filter((item) => item.shouldRender);

  return (
    <>
      {activeItems.map((item, index) => (
        <TagStatus
          id={item.id}
          title={item.title}
          colorScheme={item.colorScheme}
          icon={item.icon}
          key={index}
        />
      ))}
    </>
  );
};
